/**
 * The TMS-style summary strip above the bars (`05-reporting-marking.md` §B2).
 *
 * §B2 names six read-only fields: `Officials Name`, `Date Of Birth`, `Grading Role`,
 * `Grading Level`, `Performance Outcome` and `Status`.
 *
 * `Date Of Birth` and `Grading Level` now travel on the marking-panel read, sourced from
 * `official.date_of_birth` and `official_grading.grading_level` (migration 003) through the
 * officials repository's PROFILE projection — the roster list carries neither, because a
 * date of birth is personal data and a roster table must not broadcast forty-nine of them.
 * Both are nullable end to end and the strip renders `—` when either is absent, so a gap in
 * the roster stays a visible gap rather than an invented value. `Grading Role` shows the
 * roster role, which is what the criteria set is derived from.
 *
 * **Flag, deliberate.** `official_grading` is one row per (official, TMS `grading_role`) and
 * this build has no mapping from a roster role to a TMS grading-role code, so the repository
 * takes the official's grading row deterministically rather than guessing which row belongs
 * to the role on screen. Officials in the demonstration corpus carry one grading row each,
 * so nothing is currently ambiguous; a real TMS import with two rows for one person would
 * need that mapping before this cell can be trusted.
 *
 * `Status` is `Draft` (`statuspill--warn`) and can be nothing else in this build: §B3's
 * `Draft In TMS` state is reached only by a successful push, and write path W2 is not in
 * this slice. There is no `Final` state anywhere in the app, by design — finalising is a
 * deliberate act inside TMS.
 */

import type { DateOnly, OfficialRole } from "@hockeytd/shared";

import { bandFor } from "./outcome.js";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * `1984-03-09` → `09 Mar 1984`, the form the TMS strip shows.
 *
 * Parsed by hand rather than through `new Date(...)`, because a date of birth is a plain
 * calendar day: handing `YYYY-MM-DD` to the `Date` constructor reads it as UTC midnight and
 * then renders it in the viewer's own zone, which moves a birthday west of Greenwich back
 * by a day. Anything that is not a well-formed `YYYY-MM-DD` returns null and is rendered as
 * the honest `—`, rather than as `Invalid Date` or as a silently wrong day.
 */
export function formatDateOfBirth(value: DateOnly | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return null;

  const year = match[1] ?? "";
  const month = match[2] ?? "";
  const day = match[3] ?? "";
  const monthName = MONTHS[Number(month) - 1];
  if (monthName === undefined) return null;
  if (Number(day) < 1 || Number(day) > 31) return null;

  return `${day} ${monthName} ${year}`;
}

export interface MarkingHeaderProps {
  officialName: string;
  role: OfficialRole;
  outcome: number | null;
  /** `YYYY-MM-DD` from the panel read. Absent or null renders `—`. */
  dateOfBirth?: DateOnly | null;
  /** e.g. `International Panel (5)`. Absent or null renders `—`. */
  gradingLevel?: string | null;
}

/** The `—` every unknown cell shows, with the reason on hover rather than in the layout. */
function Unknown({ reason }: { reason: string }): React.JSX.Element {
  return (
    <span className="unmarked" title={reason}>
      —
    </span>
  );
}

export function MarkingHeader({
  officialName,
  role,
  outcome,
  dateOfBirth = null,
  gradingLevel = null,
}: MarkingHeaderProps): React.JSX.Element {
  const band = bandFor(outcome);
  const bornOn = formatDateOfBirth(dateOfBirth);

  return (
    <div className="markhead">
      <div>
        <span className="k">Officials Name</span>
        <span className="v">{officialName}</span>
      </div>
      <div>
        <span className="k">Date Of Birth</span>
        <span className="v" data-testid="date-of-birth">
          {bornOn ?? <Unknown reason="No date of birth on the roster record" />}
        </span>
      </div>
      <div>
        <span className="k">Grading Role</span>
        <span className="v">{role}</span>
      </div>
      <div>
        <span className="k">Grading Level</span>
        <span className="v" data-testid="grading-level">
          {gradingLevel ?? <Unknown reason="No grading recorded for this official" />}
        </span>
      </div>
      <div>
        <span className="k">Performance Outcome</span>
        <span className="v big">
          <span data-testid="performance-outcome">
            {outcome === null ? <span className="unmarked">—</span> : outcome}
          </span>
          <span
            className={`outcome-band ${band === null ? "band--none" : band.className}`}
            data-testid="performance-band"
          >
            {band === null ? "Not Marked" : band.label}
          </span>
        </span>
      </div>
      <div>
        <span className="k">Status</span>
        <span className="v">
          <span className="statuspill statuspill--warn">
            <span className="sdot" />
            Draft
          </span>
        </span>
      </div>
    </div>
  );
}
